import React, { useState, useRef } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useAbsenceEntries } from 'contexts/absence-entries'
import { useOrganization } from 'contexts/organization'
import { size, pick } from 'utilities/object'
import { cls } from 'utilities/dom'
import { subYears, subMonths, subQuarters } from 'date-fns'
import { getYearRange, getQuarterRange, getMonthRange } from 'pages/absence/utilities'
import Modal, { useModal } from 'modals/dialog'
import ExportAbsenceSlowMessage from './slow-message'
import Form from 'components/form/controller'
import EntitiesField from 'components/form/field/entities'
import OneOfField from 'components/form/field/one-of'
import TimeField from 'components/form/field/time'
import FieldToucher from 'components/form/toucher'
import PeopleField from 'components/form/field/people'
import Message from 'components/message'
import Actions from 'components/form/actions'
import { Plain, ButtonSubmit } from 'components/button'

const ExportAbsenceSummary = ({ salt }) => {
    const { formatMessage } = useIntl()
    const { dismiss } = useModal()

    const header = useRef()

    const groupsRef = useRef()
    const peopleRef = useRef()

    const { exportEntries } = useAbsenceEntries()
    const { organization } = useOrganization()

    const [selectionType, setSelectionType] = useState('all')
    const [groups, setGroups] = useState([])
    const [people, setPeople] = useState([])

    const [exporting, setExporting] = useState(false)
    const [error, setError] = useState(false)
    const [started, setStarted] = useState(false)

    const generate = async ({ types, time, selectionType, groups, people, ...body }) => {
        setExporting(true)

        types = (types ?? []).map(({ id }) => id)
        const [fromDate, toDate] = time ?? []

        body = {
            ...body,
            type: 'summary',
            format: 'XLSX',
            ...(types.length ? { types } : null),
            ...(fromDate ? { fromDate } : null),
            ...(toDate ? { toDate } : null)
        }

        if(selectionType === 'groups' && !!groups?.length) {
            body.groups = groups.map(group => pick(group, 'type', 'id'))
        }

        if(selectionType === 'people' && people?.length) {
            body.users = people.map(({ id }) => id)
        }

        const { ok } = await exportEntries(body)

        setExporting(false)

        if(ok) {
            setStarted(true)
            header.current?.scrollIntoView({ behavior: 'smooth' })
        } else {
            setError(true)
        }
    }

    const reset = () => {
        setError(false)
        setExporting(false)
        setStarted(false)
    }

    return (
        <>
            <Modal.Header
                heading={formatMessage({
                    id: 'absence_action_export_summary',
                    defaultMessage: 'Export absence summary'
                })}
                caption={formatMessage({
                    id: 'absence_export_type_summary_helper',
                    defaultMessage: 'Summary per absence type per person'
                })}
                ref={header} />
            <Form
                layout="vertical"
                onChange={reset}
                onSubmit={generate}>
                {({ errors, trigger }) => (
                    <>
                        <Modal.Body>
                            {started && <ExportAbsenceSlowMessage />}
                            <EntitiesField
                                name="types"
                                label={formatMessage({
                                    id: 'noun_absence_types',
                                    defaultMessage: 'Absence types'
                                })}
                                field={{
                                    placeholder: formatMessage({
                                        id: 'absence_export_absence_types_placeholder',
                                        defaultMessage: 'Pick absence types or leave empty for all'
                                    })
                                }}
                                controlProps={{ autoFocus: true }}
                                entity={{ type: 'absenceType' }}
                                picker={{
                                    heading: formatMessage({
                                        id: 'absence_types_action_pick',
                                        defaultMessage: 'Pick absence types'
                                    }),
                                    query: {
                                        key: ['absence', 'types'],
                                        path: '/absence/types',
                                        adaptItems: types => types
                                            .sort((one, two) => Number(two?.enabled ?? true) - Number(one?.enabled ?? true))
                                            .map(type => ({
                                                ...type,
                                                ...(!type?.enabled ? {
                                                    softDisabled: true,
                                                    tooltip: formatMessage({
                                                        id: 'absence_type_picker_disabled_tooltip',
                                                        defaultMessage: 'Disabled absence type. While new entries of this type can’t be created, you can choose to include already existing ones in the report.'
                                                    })
                                                } : null)
                                            }))
                                    }
                                }} />
                            <OneOfField
                                salt={salt}
                                label={formatMessage({
                                    id: 'absence_export_include_who_label',
                                    defaultMessage: 'Who should the report include?'
                                })}
                                name="selectionType"
                                field={{
                                    value: 'all',
                                    include: 'always',
                                    options: [
                                        {
                                            value: 'all',
                                            label: formatMessage({
                                                id: 'organization_everyone_in',
                                                defaultMessage: 'Everyone in {name}'
                                            }, organization),
                                            content: null
                                        },
                                        {
                                            value: 'groups',
                                            label: formatMessage({
                                                id: 'groups_members_of_generic',
                                                defaultMessage: 'Members of groups'
                                            }),
                                            controlRef: groupsRef,
                                            content: (
                                                <EntitiesField
                                                    name="groups"
                                                    label={false}
                                                    field={{ value: groups }}
                                                    picker={{
                                                        heading: formatMessage({
                                                            id: 'groups_action_pick',
                                                            defaultMessage: 'Pick groups'
                                                        }),
                                                        query: {
                                                            key: ['groups'],
                                                            path: '/groups',
                                                            filterOptions: {
                                                                overrides: {
                                                                    search: {
                                                                        controlProps: {
                                                                            placeholder: formatMessage({
                                                                                id: 'groups_action_find',
                                                                                defaultMessage: 'Find groups'
                                                                            })
                                                                        }
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }}
                                                    onChange={({ groups }) => setGroups(groups)}
                                                    ref={groupsRef} />
                                            )
                                        },
                                        {
                                            value: 'people',
                                            label: formatMessage({
                                                id: 'people_individuals',
                                                defaultMessage: 'Individual people'
                                            }),
                                            controlRef: peopleRef,
                                            content: (
                                                <PeopleField
                                                    name="people"
                                                    label={false}
                                                    field={{ value: people }}
                                                    onChange={({ people }) => setPeople(people)}
                                                    ref={peopleRef} />
                                            )
                                        }
                                    ]
                                }}
                                onChange={({ selectionType }) => setSelectionType(selectionType)} />
                            <TimeField
                                salt={salt}
                                label={formatMessage({
                                    id: 'absence_type_label_time_period',
                                    defaultMessage: 'Time period'
                                })}
                                name="time"
                                field={{
                                    value: getMonthRange({ date: subMonths(new Date(), 1) }),
                                    range: true,
                                    required: true,
                                    include: 'always',
                                    unsettable: false
                                }}
                                picker={{
                                    shortcuts: [
                                        {
                                            label: formatMessage({
                                                id: 'date_range_predefined_year_previous',
                                                defaultMessage: 'Last year'
                                            }),
                                            range: getYearRange({ date: subYears(new Date(), 1) })
                                        },
                                        {
                                            label: formatMessage({
                                                id: 'date_range_predefined_year_current',
                                                defaultMessage: 'This year'
                                            }),
                                            range: getYearRange()
                                        },
                                        {
                                            label: formatMessage({
                                                id: 'date_range_predefined_quarter_previous',
                                                defaultMessage: 'Last quarter'
                                            }),
                                            range: getQuarterRange({ date: subQuarters(new Date(), 1) })
                                        },
                                        {
                                            label: formatMessage({
                                                id: 'date_range_predefined_quarter_current',
                                                defaultMessage: 'This quarter'
                                            }),
                                            range: getQuarterRange()
                                        },
                                        {
                                            label: formatMessage({
                                                id: 'date_range_predefined_month_previous',
                                                defaultMessage: 'Last month'
                                            }),
                                            range: getMonthRange({ date: subMonths(new Date(), 1) })
                                        },
                                        {
                                            label: formatMessage({
                                                id: 'date_range_predefined_month_current',
                                                defaultMessage: 'This month'
                                            }),
                                            range: getMonthRange()
                                        }
                                    ]
                                }} />
                            <FieldToucher name="time" />
                            {!!error && (
                                <Message
                                    type="error"
                                    message={formatMessage({
                                        id: 'download_failure',
                                        defaultMessage: 'There was an error downloading the file.\nPlease try again in a short while.'
                                    })} />

                            )}
                            {!error && (
                                <Message
                                    type="info"
                                    message={formatMessage({
                                        id: 'absence_export_summary_time_period_information',
                                        defaultMessage: 'Only the absence days occurring within the specified time period will be included in the report.'
                                    })} />
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Actions>
                                <Plain
                                    onClick={dismiss}
                                    disabled={exporting}>
                                    <FormattedMessage
                                        id="action_cancel"
                                        defaultMessage="Cancel" />
                                </Plain>
                                <ButtonSubmit
                                    className={cls('constructive', exporting && 'loading')}
                                    disabled={
                                        !!size(errors) ||
                                        (selectionType === 'groups' && !groups.length) ||
                                        (selectionType === 'people' && !people.length)
                                    }
                                    ref={trigger}>
                                    <FormattedMessage
                                        id="action_generate_report"
                                        defaultMessage="Generate export" />
                                </ButtonSubmit>
                            </Actions>
                        </Modal.Footer>
                    </>
                )}
            </Form>
        </>
    )
}

export default ExportAbsenceSummary